// Keyed by window.Shopify.theme.schema_name. Kept out of section settings so a
// merchant cannot break the integration from the theme editor. A site overrides
// any key through NBD.config.loadMore in nbd-config.
const THEME_PRESETS = {
  Symmetry: {
    insertSelector: '.filter-container .product-grid',
    itemSelector: '.product-block',
    paginationSelector: '.pagination',
    filterSelector: '.utility-bar[data-ajax-container]',
    loadingClass: 'ajax-loading',
    buttonClass: 'btn btn--primary',
    wrapperClass: 'container fully-spaced-row--medium',
  },
  Dawn: {
    insertSelector: '.product-grid-container .product-grid',
    itemSelector: '.grid__item',
    paginationSelector: '.pagination',
    filterSelector: '#ProductGridContainer',
    loadingClass: 'loading',
    buttonClass: 'button button--primary',
    wrapperClass: 'page-width',
  },
};

class NBDLoadMore extends HTMLElement {
  constructor() {
    super();
    this.items = [];
    this.page = 1;
    this.request = 0;
    this.themeName = window.Shopify?.theme?.schema_name;
    const preset = THEME_PRESETS[this.themeName] || {};
    const siteConfig = window.NBD?.config?.loadMore || {};
    const option = (attribute, key) => this.getAttribute(attribute) || siteConfig[key] || preset[key];
    this.insertSelector = option('insert-selector', 'insertSelector');
    this.itemSelector = option('item-selector', 'itemSelector');
    this.paginationSelector = option('pagination-selector', 'paginationSelector');
    this.filterSelector = option('filter-selector', 'filterSelector');
    this.loadingClass = option('loading-class', 'loadingClass');
    this.buttonClass = option('button-class', 'buttonClass');
    this.wrapperClass = option('wrapper-class', 'wrapperClass');
    this.counterText = this.getAttribute('counter-text') || '';
    this.button = this.querySelector('button');
    this.counter = this.querySelector('.counter');
  }

  connectedCallback() {
    if (!this.insertSelector || !this.itemSelector) {
      console.info(`nbd-load-more: no preset for theme "${this.themeName}", keeping the theme's pagination`);
      // The stylesheet only makes it transparent, which leaves the button focusable.
      this.hidden = true;
      return;
    }
    this.applyThemeClasses();
    this.button.addEventListener('click', () => this.loadMore());
    this.handleFilters();
    this.refresh();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  applyThemeClasses() {
    if (this.buttonClass && !this.button.className) this.button.className = this.buttonClass;
    const wrapper = this.closest('[data-section-type="nbd-load-more"]');
    if (this.wrapperClass && wrapper && !wrapper.className.trim()) wrapper.className = this.wrapperClass;
  }

  // Themes swap the grid when filters change, so start again from the new first page.
  handleFilters() {
    if (!this.filterSelector || !this.loadingClass) return;
    const roots = [...document.querySelectorAll(this.filterSelector)];
    if (roots.length < 1) return;

    const hasLoadingClass = (value) => (value || '').split(/\s+/).includes(this.loadingClass);

    this.observer = new MutationObserver((mutations) => {
      let action = null;
      mutations.forEach((mu) => {
        if (mu.type === 'childList') {
          // Our own appends land deeper in the tree, so only a swap of the root's contents counts.
          if (roots.includes(mu.target)) action = 'refresh';
          return;
        }
        const was = hasLoadingClass(mu.oldValue);
        const is = mu.target.classList.contains(this.loadingClass);
        if (was === is) return;
        // Ignore loading states inside product cards, such as a quick add button.
        if (this.itemSelector && mu.target.closest(this.itemSelector)) return;
        action = is ? 'loading' : 'refresh';
      });
      if (action === 'loading') this.loading();
      if (action === 'refresh') this.refresh();
    });

    roots.forEach((el) =>
      this.observer.observe(el, {
        attributes: true,
        attributeFilter: ['class'],
        attributeOldValue: true,
        childList: true,
        subtree: true,
      })
    );
  }

  handleImages() {
    window.theme?.ProductBlockManager?.loadImages(this.container);
  }

  loading() {
    this.button.disabled = true;
    this.style.opacity = 0;
  }

  complete() {
    if (this.items.length < 1) {
      this.counter.innerText = '';
      this.button.style.display = 'none';
      return;
    }
    this.hideDefaultPagination();
    this.button.disabled = false;
    this.button.style.display = '';
    this.style.opacity = 1;
    this.counter.innerText = this.counterText.replace('[count]', this.items.length);
  }

  refresh() {
    this.loading();
    this.page = Number(new URLSearchParams(window.location.search).get('page')) || 1;
    this.prepareNextItems();
  }

  async prepareNextItems() {
    const request = ++this.request;
    const items = await this.fetchItems(this.nextPageUrl(this.page));
    // A filter change may have started a newer request while this one was in flight.
    if (request !== this.request) return;
    // Symmetry's reveal animation would leave appended items invisible.
    items.forEach((el) => el.removeAttribute('data-cc-animate'));
    this.items = items;
    this.page++;
    this.complete();
  }

  async loadMore() {
    this.loading();
    this.insertItems(this.items);
    await this.prepareNextItems();
  }

  get container() {
    return document.querySelector(`main ${this.insertSelector}`);
  }

  insertItems(items) {
    this.container.append(...items);
    this.handleImages();

    const event = new CustomEvent('nbd:collection:loaded-more', {
      detail: {
        action: 'loaded',
        count: items.length,
      },
    });

    window.dispatchEvent(event);
  }

  hideDefaultPagination() {
    document.querySelectorAll(`main ${this.paginationSelector}`).forEach((el) => (el.style.display = 'none'));
  }

  nextPageUrl(page) {
    const params = new URLSearchParams(window.location.search);
    params.set('page', page + 1);
    const sectionId = this.container?.closest('[data-section-id]')?.getAttribute('data-section-id');
    if (sectionId) {
      params.set('section_id', sectionId);
    }
    return `${window.location.pathname}?${params.toString()}`;
  }

  async fetchItems(url) {
    try {
      const res = await fetch(url);
      if (!res.ok) return [];
      const html = new DOMParser().parseFromString(await res.text(), 'text/html');
      return [...html.querySelectorAll(`${this.insertSelector} ${this.itemSelector}`)];
    } catch (err) {
      console.error('nbd-load-more', err);
      return [];
    }
  }
}

if (!customElements.get('nbd-load-more')) {
  customElements.define('nbd-load-more', NBDLoadMore);
}
